'use client';

import style from './PageClient.module.scss';
import { useState, useEffect, useCallback } from 'react';
import Search from '@/components/ui/search/Search';
import CardView from '@/components/ui/cardView/CardView';
import Avatar from '@/components/ui/avatar/Avatar';
import Coin from '@/components/ui/coin/Coin';
import Link from 'next/link';
import { WalletService } from '@/libs/services/wallet';
import type { CoinData } from '@/types/wallet';
import { showApiError } from '@/libs/helpers/showApiState';
import Image from 'next/image';
import { v4 as uuid } from 'uuid';
import { useListSearch } from '@/hooks/useListSearch';
import { useAppDispatch } from '@/store/hook';
import { startGlobalLoading, stopGlobalLoading } from '@/features/ui/uiLoadingSlice';
import { Skeleton } from 'antd';
import { useGlobalSkeletonLoading } from '@/hooks/useGlobalSkeletonLoading';
import { EmptyState } from '@/components/ui/emptyState';
import { IconCoinOutline } from '@/components/icons';

const RedCoinCollectList = () => {
  const isLoading = useGlobalSkeletonLoading(1000);
  const dispatch = useAppDispatch();
  const [redCoinsListData, setRedCoinsListData] = useState<CoinData[] | null>(null);
  const hasNoData = redCoinsListData?.length === 0;
  const { keyword, setKeyword, filtered } = useListSearch<CoinData>({
    source: redCoinsListData,
    searchBy: (coin) => coin.name ?? '', // 避免 null 問題
  });
  const getCoinsListInfo = useCallback(async () => {
    dispatch(startGlobalLoading());
    try {
      const result = await WalletService.getRedCoinsList();
      const resultWithId = result.map((r) => ({
        ...r,
        _key: uuid(),
      }));
      setRedCoinsListData(resultWithId);
    } catch (err) {
      showApiError(err, '取得籌碼包資訊失敗');
    } finally {
      dispatch(stopGlobalLoading());
    }
  }, [dispatch]);

  useEffect(() => {
    getCoinsListInfo();
  }, [getCoinsListInfo]);

  return (
    <>
      {!hasNoData ? (
        <CardView>
          {/* 功能按鈕區 */}
          <div className="d-flex justify-content-end mb-10 gap-3">
            <Search value={keyword} onChange={setKeyword} />

            {/* <button type="button" className="px-2 line-height-none">
          <IconFilter width={22} />
        </button> */}
          </div>

          {/* 列表區 */}
          <div className="row gy-10">
            {!isLoading
              ? (filtered ?? []).map((coin) => (
                  <div
                    key={coin._key}
                    className="col-4 col-md-3 col-lg-3 d-flex flex-column align-items-center"
                  >
                    <Link href={`/user/${coin.open_id}`} className="position-relative">
                      <Coin color="red" scale={0.9}>
                        <Avatar src={coin.photo} />
                      </Coin>
                      <div className="text-center">
                        <span className={style.numTag} style={{ color: '#d3745d' }}>
                          {coin.total}
                        </span>
                      </div>
                      {coin.emoji_photo && (
                        <span className="position-absolute top-0 start-0">
                          <Image
                            src={coin.emoji_photo}
                            alt={coin.emoji_id}
                            width={28}
                            height={28}
                          />
                        </span>
                      )}
                    </Link>

                    <span className="pb-1 text-truncate w-100 text-center fs-sm-1">
                      {coin.name}
                    </span>
                    <button
                      type="button"
                      className={`text-truncate ${style.badge}`}
                      style={{
                        backgroundColor: `${
                          coin.is_bday ? '#EB76A1' : coin.is_new_coin ? '#FF9149' : '#1E9FF2'
                        }`,
                      }}
                    >
                      {coin.is_bday ? '本週壽星' : ''}
                    </button>
                  </div>
                ))
              : Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="col-4 col-md-3 col-lg-3">
                    <Skeleton.Avatar active size={100} shape="circle" />
                  </div>
                ))}
          </div>
        </CardView>
      ) : (
        <EmptyState title="目前尚未持有紅籌碼" icon={<IconCoinOutline width={36} />} />
      )}
    </>
  );
};

export default RedCoinCollectList;
